<template>
  <div class="admin-dashboard">
    <h1>Admin Dashboard</h1>

    <!-- Main Tab Navigation -->
    <div class="main-tab-navigation">
      <button
        :class="['main-tab-btn', { active: activeMainTab === 'users' }]"
        @click="activeMainTab = 'users'"
      >
        Users & Assessments
      </button>
      <button
        :class="['main-tab-btn', { active: activeMainTab === 'translations' }]"
        @click="activeMainTab = 'translations'"
      >
        Translations Editor
      </button>
      <button
        :class="['main-tab-btn', { active: activeMainTab === 'browser' }]"
        @click="activeMainTab = 'browser'"
      >
        Database Browser
      </button>
    </div>

    <!-- Users Tab Content -->
    <div v-if="activeMainTab === 'users'" class="main-tab-content">

    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card">
        <h3>TOTAL</h3>
        <div class="card-content">
          <div class="metric">
            <p class="summary-number">{{ totalAccounts }}</p>
            <p class="summary-label">Accounts</p>
          </div>
          <div class="metric">
            <p class="summary-number">{{ totalAssessments }}</p>
            <p class="summary-label">Assessments</p>
          </div>
        </div>
      </div>
      <div class="summary-card">
        <h3>REPS</h3>
        <div class="card-content">
          <div class="metric">
            <p class="summary-number">{{ repAccounts }}</p>
            <p class="summary-label">Accounts</p>
          </div>
          <div class="metric">
            <p class="summary-number">{{ repAssessments }}</p>
            <p class="summary-label">Assessments</p>
          </div>
        </div>
      </div>
      <div class="summary-card">
        <h3>CUSTOMERS</h3>
        <div class="card-content">
          <div class="metric">
            <p class="summary-number">{{ customerAccounts }}</p>
            <p class="summary-label">Accounts</p>
          </div>
          <div class="metric">
            <p class="summary-number">{{ customerAssessments }}</p>
            <p class="summary-label">Assessments</p>
          </div>
        </div>
      </div>
    </div>

    <div class="sorting-controls">
      <label for="sort-select">Sort by: </label>
      <select id="sort-select" v-model="sortBy">
        <option value="alphabetical">Alphabetical</option>
        <option value="newest">Newest Account</option>
        <option value="recent">Most Recent Assessment</option>
        <option value="customer">Customer Assessments Only</option>
        <option value="rep">Rep Assessments Only</option>
      </select>
    </div>

    <div v-if="loading">Loading...</div>
    <div v-else-if="error">{{ error }}</div>
    <div v-else class="admin-dash-assessments">
      <div v-for="user in sortedUsers" :key="user.id" class="user-card">
        <div class="user-header">
          <h3 class="admin-name">{{ user.name }}</h3>
          <div class="user-actions">
            <button @click="editUser(user)" class="edit-btn">Edit</button>
            <button @click="confirmDeleteUser(user)" class="delete-btn">Delete</button>
          </div>
        </div>
        
        <div class="admin-email"><a :href="'mailto:' + user.email">{{ user.email }}</a></div>
        <h5 class="admin-company">Company: <div class="admin-text">{{ user.company }}</div></h5>
        <h5 class="admin-industry">Industry: <div class="admin-text">{{ user.industry }}</div></h5>
        <h5 class="admin-user-type">Type: <div class="admin-text">{{ user.isRep ? 'Rep' : 'Customer' }}</div></h5>
        <h5 class="admin-created">Account Created: <div class="admin-text">{{ formatDate(user.createdAt) }}</div></h5>
        <div class="cleaner"></div>
        
        <div class="admin-assessments">Assessments:</div>
        <ul class="admin-list">
          <!-- Rep Assessments -->
          <template v-for="assessment in user.assessments" :key="'rep-'+assessment.id">
            <li class="assessment-item rep-assessment">
              <button @click="viewAssessment(assessment.id, false)" class="view-btn">View</button>
              {{ assessment.title }}
              <div class="cleaner"></div>
              <div class="assessment-tag rep">Rep</div>
              <span class="admin-date">Started: {{ formatDate(assessment.createdAt) }}</span>
            </li>
          </template>
          <!-- Customer Assessments -->
          <template v-for="assessment in user.customerAssessments" :key="'cust-'+assessment.id">
            <li class="assessment-item customer-assessment">
              <button @click="viewAssessment(assessment.id, true)" class="view-btn">View</button>
              {{ assessment.title }}
              <div class="cleaner"></div>
              <div class="assessment-tag customer">Customer</div>
              <span class="admin-date">Started: {{ formatDate(assessment.createdAt) }}</span>
            </li>
          </template>
        </ul>
        <div class="cleaner"></div>
      </div>
    </div>

    </div>
    <!-- End Users Tab Content -->

    <!-- Translations Tab Content -->
    <div v-if="activeMainTab === 'translations'" class="main-tab-content">
      <AdminTranslations />
    </div>

    <!-- Database Browser Tab Content -->
    <div v-if="activeMainTab === 'browser'" class="main-tab-content">
      <AdminTranslationsBrowser />
    </div>

    <!-- Edit User Modal -->
    <div v-if="editingUser" class="modal-overlay" @click="cancelEdit">
      <div class="modal" @click.stop>
        <h3>Edit User</h3>
        <form @submit.prevent="saveUser">
          <div class="form-group">
            <label>Name:</label>
            <input v-model="editingUser.name" required>
          </div>
          <div class="form-group">
            <label>Email:</label>
            <input v-model="editingUser.email" type="email" required>
          </div>
          <div class="form-group">
            <label>Company:</label>
            <input v-model="editingUser.company">
          </div>
          <div class="form-group">
            <label>Industry:</label>
            <select v-model="editingUser.industry">
              <option value="Independent Restaurant">Independent Restaurant</option>
              <option value="Restaurant Group">Restaurant Group</option>
              <option value="Hotel">Hotel</option>
              <option value="Catering">Catering</option>
              <option value="Chain Restaurant">Chain Restaurant</option>
              <option value="Supermarket">Supermarket</option>
              <option value="Convenience Store">Convenience Store</option>
              <option value="K-12 School">K-12 School</option>
              <option value="College & University">College & University</option>
              <option value="Corrections">Corrections</option>
              <option value="Military/Government">Military/Government</option>
              <option value="Business & Industry">Business & Industry</option>
              <option value="Cambro Rep">Cambro Rep</option>
            </select>
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="editingUser.isRep"> Cambro Rep
            </label>
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" v-model="editingUser.isAdmin"> Admin Access
            </label>
          </div>
          <div class="modal-actions">
            <button type="submit" class="save-btn">Save</button>
            <button type="button" @click="cancelEdit" class="cancel-btn">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import AdminTranslations from './AdminTranslations.vue';
import AdminTranslationsBrowser from './AdminTranslationsBrowser.vue';

export default {
  name: 'AdminDashboard',
  components: {
    AdminTranslations,
    AdminTranslationsBrowser
  },
  setup() {
    const activeMainTab = ref('users');
    const router = useRouter();
    const users = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const sortBy = ref('alphabetical');
    const editingUser = ref(null);

    // Summary computeds
    const totalAccounts = computed(() => users.value.length);
    const repAccounts = computed(() => users.value.filter(u => u.isRep).length);
    const customerAccounts = computed(() => users.value.filter(u => !u.isRep).length);
    const totalAssessments = computed(() => 
      users.value.reduce((total, user) => 
        total + (user.assessments?.length || 0) + (user.customerAssessments?.length || 0), 0)
    );
    const repAssessments = computed(() => 
      users.value.reduce((total, user) => total + (user.assessments?.length || 0), 0)
    );
    const customerAssessments = computed(() => 
      users.value.reduce((total, user) => total + (user.customerAssessments?.length || 0), 0)
    );

    const sortedUsers = computed(() => {
      if (!users.value) return [];
      
      let filteredUsers = [...users.value];

      if (sortBy.value === 'customer') {
        filteredUsers = filteredUsers.filter(user => user.customerAssessments?.length > 0);
      } else if (sortBy.value === 'rep') {
        filteredUsers = filteredUsers.filter(user => user.assessments?.length > 0);
      }

      if (sortBy.value === 'alphabetical') {
        return filteredUsers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else if (sortBy.value === 'newest') {
        return filteredUsers.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      } else if (sortBy.value === 'recent') {
        return filteredUsers.sort((a, b) => {
          const getAllAssessments = (user) => [
            ...(user.assessments || []),
            ...(user.customerAssessments || [])
          ];
          const latestA = getAllAssessments(a).length ? Math.max(...getAllAssessments(a).map(assessment => new Date(assessment.createdAt || 0))) : 0;
          const latestB = getAllAssessments(b).length ? Math.max(...getAllAssessments(b).map(assessment => new Date(assessment.createdAt || 0))) : 0;
          return latestB - latestA;
        });
      }
      return filteredUsers;
    });

    const fetchAdminData = async () => {
      try {
        loading.value = true;
        const response = await axios({
          method: 'get',
          url: '/storesafe/api/admin/users-with-assessments',
          withCredentials: true
        });
        
        if (response.data && Array.isArray(response.data)) {
          users.value = response.data;
        } else {
          error.value = 'Invalid data format received from server';
        }
      } catch (err) {
        console.error('Admin data error:', err);
        error.value = err.response?.data?.message || 'Error fetching admin data';
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const viewAssessment = (id, isCustomer) => {
      router.push({
        path: `/results/${id}`,
        query: { 
          type: isCustomer ? 'customer' : 'rep',
          adminView: true,
          userId: String(users.value.find(u => 
            u.assessments?.some(a => a.id === id) || 
            u.customerAssessments?.some(a => a.id === id)
          )?.id || '')
        }
      });
    };

    const editUser = (user) => {
      editingUser.value = { ...user };
    };

    const cancelEdit = () => {
      editingUser.value = null;
    };

    const saveUser = async () => {
      try {
        const response = await axios.put(`/api/admin/users/${editingUser.value.id}`, editingUser.value, {
          withCredentials: true,
          baseURL: '/storesafe'
        });
        
        // Update local data
        const index = users.value.findIndex(u => u.id === editingUser.value.id);
        if (index !== -1) {
          users.value[index] = { ...users.value[index], ...editingUser.value };
        }
        
        editingUser.value = null;
      } catch (err) {
        console.error('Error updating user:', err);
        alert('Failed to update user: ' + (err.response?.data?.message || err.message));
      }
    };

    const confirmDeleteUser = (user) => {
      if (confirm(`Are you sure you want to delete ${user.name}? This will also delete all their assessments.`)) {
        deleteUser(user);
      }
    };

    const deleteUser = async (user) => {
      try {
        await axios.delete(`/api/admin/users/${user.id}`, {
          withCredentials: true,
          baseURL: '/storesafe'
        });
        
        // Remove from local data
        users.value = users.value.filter(u => u.id !== user.id);
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user: ' + (err.response?.data?.message || err.message));
      }
    };

    const toggleUserType = async (user) => {
      const newType = !user.isRep;
      const warning = newType 
        ? 'Switching to Rep will move customer assessments to the rep assessment table.'
        : 'Switching to Customer will move rep assessments to the customer assessment table.';
      
      if (confirm(`${warning}\n\nAre you sure you want to change ${user.name} from ${user.isRep ? 'Rep' : 'Customer'} to ${newType ? 'Rep' : 'Customer'}?`)) {
        try {
          await axios.put(`/api/admin/users/${user.id}/toggle-type`, {}, {
            withCredentials: true,
            baseURL: '/storesafe'
          });
          
          // Refresh data
          await fetchAdminData();
        } catch (err) {
          console.error('Error toggling user type:', err);
          alert('Failed to change user type: ' + (err.response?.data?.message || err.message));
        }
      }
    };

    onMounted(fetchAdminData);

    return {
      activeMainTab,
      users,
      loading,
      error,
      sortBy,
      sortedUsers,
      editingUser,
      totalAccounts,
      repAccounts,
      customerAccounts,
      totalAssessments,
      repAssessments,
      customerAssessments,
      formatDate,
      viewAssessment,
      editUser,
      cancelEdit,
      saveUser,
      confirmDeleteUser,
      toggleUserType
    };
  }
};
</script>

<style scoped>
.main-tab-navigation {
  display: flex;
  gap: 10px;
  margin-bottom: 25px;
  border-bottom: 2px solid #ddd;
}

.main-tab-btn {
  padding: 12px 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 18px;
  font-weight: 500;
  color: #666;
  border-bottom: 3px solid transparent;
  transition: all 0.3s;
}

.main-tab-btn:hover {
  color: #ee3e41;
}

.main-tab-btn.active {
  color: #ee3e41;
  border-bottom-color: #ee3e41;
}

.main-tab-content {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

@media (max-width: 768px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }
}

.summary-card {
  background: white;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  text-align: center;
}

.summary-card h3 {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.card-content {
  display: flex;
  justify-content: space-around;
  gap: 20px;
}

.metric {
  flex: 1;
}

.summary-number {
  font-size: 3rem;
  font-weight: bold;
  color: #ee3e41;
  margin: 0;
  line-height: 1;
}

.summary-label {
  font-size: 16px;
  color: #333;
  margin: 0;
  font-weight: 500;
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.user-actions {
  display: flex;
  gap: 10px;
}

.edit-btn, .delete-btn, .toggle-type-btn {
  padding: 4px 8px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.edit-btn {
  background: #007bff;
  color: white;
}

.delete-btn {
  background: #dc3545;
  color: white;
}

.toggle-type-btn {
  background: #28a745;
  color: white;
  margin-left: 10px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  padding: 30px;
  border-radius: 8px;
  width: 500px;
  max-width: 90vw;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.form-group input, .form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-group input[type="checkbox"] {
  width: auto;
  margin-right: 8px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.save-btn {
  background: #28a745;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-btn {
  background: #6c757d;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.admin-user-type {
  display: flex;
  align-items: center;
}
</style>